/* eslint-disable max-lines */
import React, { useContext, useEffect, useState } from 'react'
import { ArrowForwardIcon } from '@chakra-ui/icons'
import { chakra, HStack, VStack } from '@chakra-ui/react'
import { useStripe, useElements } from '@stripe/react-stripe-js'
import {
  PaymentIntent,
  PaymentIntentResult,
  PaymentRequest,
  StripeError,
  StripePaymentElement
} from '@stripe/stripe-js'
import { EnvironmentEnum } from 'enums/common'
import EFeatureFlags from 'enums/featureFlags'
import { EBreakPoint } from 'enums/theme'
import useBreakPoint from 'hooks/useBreakPoint'
import get from 'lodash/get'
import { observer } from 'mobx-react'
import { useFormContext } from 'react-hook-form'
import { useMediaQuery } from 'react-responsive'
import { toast } from 'react-toastify'
import { FlagValue, useFlag } from 'react-unleash-flags'
import { CommonError } from 'types'
import { handleError } from 'API/error'
import { updatePaymentInfo } from 'API/houseApplication'
import { authenticateCRS } from 'API/houseApplication/crs'
import { IApplicationFormValues } from 'interfaces/application'
import { convertNumberToUSD, convertUSDToCent } from 'utils/common'
import { ApplicationContext } from '../../../../applicationForm.context'
import { BottomBackButton, BottomButton } from '../../../../applicationFormModalV2.styles'
import { DESKTOP_CONTAINER_WIDTH, EBottomButtonValue, TABLET_CONTAINER_WIDTH } from '../../../../constant'
import { getCreditScore, handleUploadCriminalReport } from '../../../../utils'
import { CompleteScreeningContext } from '../../completeScreening.context'
import { ECompleteScreeningSteps } from '../../utils'
import CheckoutFormMainSection from './components/CheckoutFormMainSection'
import PaymentSuccessful from './components/PaymentSuccessful'
import { EPaymentErrorType, ICheckoutFormProps, MIN_FICO_CREDIT_SCORE } from './constants'

const isProduction: boolean = process.env.NODE_ENV === EnvironmentEnum.PRODUCTION

const CheckoutForm = (props: ICheckoutFormProps) => {
  const { isLoading, setIsLoading, setIsFailed, amount } = props
  const {
    isBackLoading,
    handlePreviousStep,
    isPaymentSuccessful,
    setIsPaymentSuccessful,
    paymentClientSecret,
    setCompleteScreeningStep
  } = useContext(ApplicationContext)
  const { nextStep } = useContext(CompleteScreeningContext)
  const stripe = useStripe()
  const elements = useElements()
  const { getValues } = useFormContext()
  const crsCheckFeatureFlagName: string = `${process.env.REACT_APP_FLAGS_CTX_APP_NAME}-${EFeatureFlags.WEBSITE_APPLICATION_FORM_CRS_CHECK}`
  const crsCheckFeatureFlag: FlagValue | undefined = useFlag(crsCheckFeatureFlagName)
  const isEnableCRSCheckFeature: boolean | undefined = crsCheckFeatureFlag && crsCheckFeatureFlag?.enabled
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)
  const [isReady, setIsReady] = useState<boolean>(false)
  const isMobile: boolean = useBreakPoint(EBreakPoint.BASE, EBreakPoint.MD)
  const isOverflow: boolean = useMediaQuery({ maxHeight: isMobile ? 850 : 800 }) && !isPaymentSuccessful
  async function checkBackgroundInformation(): Promise<void> {
    const formValues: IApplicationFormValues = getValues()
    await authenticateCRS()
    const [creditScore] = await Promise.all([getCreditScore(formValues), handleUploadCriminalReport(formValues)])
    // INFO: after payment will check FICO credit score if the credit score less than 550 the new lead can complete application form
    // or always valid when disable CRS check feature
    // or the new lead don't have credit score
    const isValidCreditScore: boolean =
      creditScore >= MIN_FICO_CREDIT_SCORE || !isEnableCRSCheckFeature || creditScore === 0
    if (!isValidCreditScore) {
      setIsFailed(true)
    }
  }

  const onSubmit = async (event: { preventDefault: () => void; stopPropagation: () => void }): Promise<void> => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault()
    event.stopPropagation()

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }
    setIsLoading(true)
    try {
      if (!isPaymentSuccessful) {
        const confirmPaymentPromise: Promise<PaymentIntentResult> = new Promise(async (resolve, reject) => {
          const paymentResponse: PaymentIntentResult = await stripe.confirmPayment({
            //`Elements` instance that was used ko create the Payment Element
            elements,
            redirect: 'if_required'
          })
          if (paymentResponse?.error) {
            reject(paymentResponse?.error)
          }
          resolve(paymentResponse)
        })
        //*INFO: Confirm the Payment
        const paymentIntentResult: PaymentIntentResult = await toast.promise(confirmPaymentPromise, {
          pending: 'Your payment is processing.',
          success: 'Payment succeeded!'
        })
        const error: StripeError | undefined = paymentIntentResult?.error
        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error) {
          return
        }
        if (isEnableCRSCheckFeature) {
          await checkBackgroundInformation()
        }
        const { paymentIntent } = paymentIntentResult
        if (paymentIntent) {
          const phoneNumber: string = getValues('phoneNumber')
          const status: PaymentIntent.Status = paymentIntent?.status
          await updatePaymentInfo(phoneNumber, status, paymentIntent?.id)
        }
        typeof setIsPaymentSuccessful === 'function' && setIsPaymentSuccessful(true)
        nextStep()
      } else {
        nextStep()
        setIsLoading(false)
      }
      typeof setCompleteScreeningStep === 'function' && setCompleteScreeningStep(ECompleteScreeningSteps.DRIVER_LICENSE)
    } catch (error) {
      const errorType: string = get(error, 'type', '') || JSON.stringify(error)
      const errorMessage: string = get(error, 'message', 'Your payment was not successful, please try again')
      if (errorType === EPaymentErrorType.VALIDATION_ERROR) {
        return
      }
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!stripe) {
      return
    }
    // *INFO: Amount using for testing will scale down 1:100
    const testingAmount: number = amount / 100
    if (stripe) {
      const request: PaymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Apply Fee',
          amount: isProduction ? convertUSDToCent(amount) : convertUSDToCent(testingAmount)
        },
        requestPayerName: true
      })
      // Check the availability of the Payment Request API.
      request.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(request)
        }
      })
    }
  }, [stripe])

  useEffect(() => {
    if (elements) {
      const stripeElement: StripePaymentElement | null = elements.getElement('payment')
      if (stripeElement) {
        stripeElement.on('ready', () => {
          setIsReady(true)
        })
      }
      if (isPaymentSuccessful) {
        setIsReady(true)
      }
    }
  }, [elements])

  useEffect(() => {
    const interval = setInterval(() => {
      if (stripe && elements && paymentClientSecret && !isPaymentSuccessful) {
        stripe
          .retrievePaymentIntent(paymentClientSecret)
          .then(async ({ paymentIntent }) => {
            if (paymentIntent?.status) {
              const phoneNumber: string = getValues('phoneNumber')
              await updatePaymentInfo(phoneNumber, paymentIntent.status, paymentIntent?.id)
            }
          })
          .catch((error) => {
            handleError(
              error as CommonError,
              'components/pages/DetailPage/components/ApplicationFormModalV2/components/CompleteScreening/components/CheckoutForm',
              'updatePaymentInformation'
            )
          })
      }
    }, 15000)
    return () => clearInterval(interval)
  }, [stripe, elements])

  return (
    <chakra.form
      width="full"
      height={{ base: 'full', md: 'unset' }}
      onSubmit={onSubmit}
      display="flex"
      flexDirection="column"
      justifyContent={{ base: 'space-between', lg: 'flex-start' }}
      gap={8}
      marginTop={{ base: '2px', lg: 32 }}
    >
      {!isPaymentSuccessful ? (
        <CheckoutFormMainSection amount={amount} paymentRequest={paymentRequest} />
      ) : (
        <PaymentSuccessful />
      )}
      <VStack hidden={!isReady}>
        <HStack
          width="full"
          maxWidth={{ base: 'full', md: TABLET_CONTAINER_WIDTH, lg: DESKTOP_CONTAINER_WIDTH }}
          alignItems="flex-end"
          marginTop={{ md: isOverflow ? 0 : 5, lg: 'unset' }}
          justifyContent="space-between"
          paddingX={{ base: isOverflow ? 0 : 4, lg: 'unset' }}
          position={{
            base: isOverflow ? 'relative' : 'absolute',
            md: isOverflow ? 'relative' : 'absolute',
            lg: 'relative'
          }}
          bottom={{ base: isOverflow ? -5 : 6, md: isOverflow ? -5 : 12, lg: 'unset' }}
        >
          <BottomBackButton isLoading={isBackLoading} onClick={handlePreviousStep} variant="solid">
            {EBottomButtonValue.BACK}
          </BottomBackButton>
          <BottomButton
            colorScheme="teal"
            variant="solid"
            onClick={onSubmit}
            bottom="unset"
            isLoading={isLoading || !stripe || !elements}
            rightIcon={isPaymentSuccessful ? <ArrowForwardIcon /> : undefined}
            iconSpacing={isPaymentSuccessful ? 2 : undefined}
            id="submit"
          >
            {isPaymentSuccessful ? 'Continue' : `Pay ${convertNumberToUSD(amount)}`}
          </BottomButton>
        </HStack>
      </VStack>
    </chakra.form>
  )
}

export default observer(CheckoutForm)
